import { APP_PATHS } from './appRoutes';

/**
 * Fallback navigation configuration interface.
 * Supports hierarchical navigation with optional subLinks for grouped menus.
 */
export interface FallbackNavLink {
    name: string;
    path?: string;
    icon: string;
    subLinks?: FallbackNavLink[];
}

/**
 * Maps URL path prefixes to their corresponding navigation shell.
 * Used by getFallbackNavigation to select the correct sidebar links
 * based on the user's defaultPath without hardcoding strings in the hook.
 */
export const SHELL_PREFIXES = {
    ADMIN: '/admin',
    STUDENT: '/student',
    STAFF: '/staff',
} as const;

/**
 * Maps role keyword signatures (uppercase substrings) to their navigation shell.
 * Used as the secondary fallback when defaultPath is absent or does not match
 * a known shell prefix.
 */
export const ROLE_SIGNATURES = {
    ADMIN: 'ADMIN',
    STUDENT: 'STUDENT',
} as const;

/**
 * Client-side storage cache configuration for navigation data.
 *
 * SECURITY NOTE: CACHE_KEY_PREFIX is a localStorage *namespace prefix*, not a secret.
 * It is intentionally visible in the browser — it only scopes non-sensitive navigation
 * structure (links the backend already authorized). Zero Frontend Trust is upheld by
 * the backend re-validating the JWT on every request, not by obscuring storage keys.
 *
 * CACHE_TTL_SECONDS: Sliding-window expiry after which the cache is considered stale
 * and a fresh fetch is triggered in the background.
 */
export const NAV_CACHE_CONFIG = {
    KEY_PREFIX: 'nav-links-',
    TTL_SECONDS: 120,
    MAX_CACHE_AGE_SECONDS: 3600, // 1 hour hard ceiling boundary to prevent indefinite sliding window
} as const;

/**
 * Minimum viable fallback navigation for unknown shell types (e.g. custom dynamic roles
 * whose defaultPath does not match any known shell prefix).
 * Guarantees NO role ever renders a blank sidebar — a secure and consistent UX baseline.
 */
export const UNIVERSAL_FALLBACK: FallbackNavLink[] = [
    { name: 'Dashboard', path: '/', icon: 'DashboardIcon' },
    { name: 'Profile', path: '/profile', icon: 'ProfileIcon' },
];

/**
 * Role-specific fallback navigation configurations.
 * Uses APP_PATHS constants for path values (no hardcoded paths).
 * 
 * These are used ONLY when the backend API is unavailable.
 * The primary source of truth is the database via /ui/navigation API.
 */

export const ADMIN_FALLBACK: FallbackNavLink[] = [
    { name: 'Dashboard', path: APP_PATHS.ADMIN.DASHBOARD, icon: 'DashboardIcon' },
    { name: 'Notifications', path: APP_PATHS.ADMIN.NOTIFICATIONS, icon: 'BellIcon' },
    {
        name: 'nav_academic_progression',
        icon: 'GraduationCapIcon',
        subLinks: [
            { name: 'nav_academic_sessions', path: APP_PATHS.ADMIN.ACADEMIC_SESSIONS, icon: 'CalendarIcon' },
            { name: 'nav_academic_record_entry', path: APP_PATHS.ADMIN.ACADEMIC_RECORD_ENTRY, icon: 'DocumentCollectionIcon' },
            { name: 'nav_eligibility_config', path: APP_PATHS.ADMIN.ELIGIBILITY_CONFIG, icon: 'SettingsIcon' },
        ],
    },
    {
        name: 'User Management',
        icon: 'UsersIcon',
        subLinks: [
            { name: 'User List', path: APP_PATHS.ADMIN.USER_MANAGEMENT.ROOT, icon: 'UsersIcon' },
            { name: 'Create User', path: APP_PATHS.ADMIN.USER_MANAGEMENT.CREATE, icon: 'UsersIcon' },
            { name: 'Bulk Create Users', path: APP_PATHS.ADMIN.USER_MANAGEMENT.BULK_CREATE, icon: 'UsersIcon' },
        ],
    },
    {
        name: 'Scholarship Management',
        icon: 'ScholarshipIcon',
        subLinks: [
            { name: 'Programs', path: APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.ROOT, icon: 'ScholarshipIcon' },
            { name: 'Applications', path: APP_PATHS.ADMIN.SCHOLARSHIP_MANAGEMENT.APPLICATIONS, icon: 'ScholarshipIcon' },
        ],
    },
    { name: 'Feedback Management', path: APP_PATHS.ADMIN.FEEDBACK_MANAGEMENT, icon: 'FeedbackIcon' },
    {
        name: 'System Configuration',
        icon: 'SettingsIcon',
        subLinks: [
            { name: 'General Settings', path: APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROOT, icon: 'SettingsIcon' },
            { name: 'Roles & Permissions', path: APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.ROLES, icon: 'SettingsIcon' },
            { name: 'Navigation Settings', path: APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.NAVIGATION, icon: 'SettingsIcon' },
            { name: 'Dynamic UI', path: APP_PATHS.ADMIN.SYSTEM_CONFIGURATION.DYNAMIC_UI, icon: 'SettingsIcon' },
        ],
    },
    {
        name: 'Reports & Analytics',
        icon: 'ReportsIcon',
        subLinks: [
            { name: 'Reports & Logs', path: APP_PATHS.ADMIN.REPORTS_LOGS, icon: 'ReportsIcon' },
            { name: 'Analytics', path: APP_PATHS.ADMIN.ANALYTICS, icon: 'ReportsIcon' },
            { name: 'Financial Insights', path: APP_PATHS.ADMIN.FINANCIAL_INSIGHTS, icon: 'PaymentIcon' },
        ],
    },
    {
        name: 'Workflows',
        icon: 'SettingsIcon',
        subLinks: [
            { name: 'Workflow Monitor', path: APP_PATHS.ADMIN.WORKFLOWS.ROOT, icon: 'SettingsIcon' },
            { name: 'Forensic Audit', path: APP_PATHS.ADMIN.WORKFLOWS.FORENSIC_AUDIT, icon: 'VerifyIcon' },
            { name: 'Workflow Health', path: APP_PATHS.ADMIN.WORKFLOWS.OBSERVABILITY, icon: 'ReportsIcon' },
        ],
    },
    { name: 'Audit Logs', path: APP_PATHS.ADMIN.AUDIT_LOGS, icon: 'VerifyIcon' },
    { name: 'Communication Rules', path: APP_PATHS.ADMIN.COMMUNICATION_RULES.ROOT, icon: 'AnnounceIcon' },
    { name: 'Profile Settings', path: APP_PATHS.ADMIN.PROFILE_SETTINGS, icon: 'ProfileIcon' },
];

export const STAFF_FALLBACK: FallbackNavLink[] = [
    { name: 'Dashboard', path: APP_PATHS.STAFF.DASHBOARD, icon: 'DashboardIcon' },
    { name: 'Profile', path: APP_PATHS.STAFF.PROFILE, icon: 'ProfileIcon' },
    { name: 'Notifications', path: APP_PATHS.STAFF.NOTIFICATIONS, icon: 'AnnounceIcon' },
    { name: 'Payment Verification', path: APP_PATHS.STAFF.PAYMENT_VERIFICATION, icon: 'VerifyIcon' },
    { name: 'Payment Trends', path: APP_PATHS.STAFF.PAYMENT_TRENDS, icon: 'ReportsIcon' },
    { name: 'Student Inquiry', path: APP_PATHS.STAFF.STUDENT_ACCOUNT_INQUIRY, icon: 'UsersIcon' },
    { name: 'Fee Structures', path: APP_PATHS.STAFF.FEE_STRUCTURE_MANAGEMENT, icon: 'FeeIcon' },
    { name: 'Fee Management', path: APP_PATHS.STAFF.FEE_MANAGEMENT, icon: 'FeeIcon' },
    { name: 'Installment Plans', path: APP_PATHS.STAFF.INSTALLMENT_PLAN_CONFIGURATION, icon: 'InstallmentIcon' },
    { name: 'Financial Reports', path: APP_PATHS.STAFF.FINANCIAL_REPORTS, icon: 'ReportsIcon' },
    { name: 'Announcements', path: APP_PATHS.STAFF.ANNOUNCEMENTS, icon: 'AnnounceIcon' },
    { name: 'Scholarship Approvals', path: APP_PATHS.STAFF.SCHOLARSHIP_APPROVALS.ROOT, icon: 'ScholarshipIcon' },
    { name: 'Assigned Feedback', path: APP_PATHS.STAFF.FEEDBACK.ROOT, icon: 'FeedbackIcon' },
    { name: 'nav_academic_sessions', path: APP_PATHS.STAFF.ACADEMIC_SESSIONS, icon: 'ScholarshipIcon' },
];

export const STUDENT_FALLBACK: FallbackNavLink[] = [
    { name: 'nav_student_dashboard', path: APP_PATHS.STUDENT.DASHBOARD, icon: 'DashboardIcon' },
    { name: 'nav_student_profile', path: APP_PATHS.STUDENT.PROFILE, icon: 'ProfileIcon' },
    { name: 'nav_student_my_scholarships', path: APP_PATHS.STUDENT.MY_SCHOLARSHIPS, icon: 'ScholarshipIcon' },
    { name: 'nav_student_make_payment', path: APP_PATHS.STUDENT.MAKE_PAYMENT, icon: 'PaymentIcon' },
    { name: 'nav_student_payment_status', path: APP_PATHS.STUDENT.PAYMENT_STATUS, icon: 'VerifyIcon' },
    { name: 'nav_student_payment_history', path: APP_PATHS.STUDENT.PAYMENT_HISTORY, icon: 'HistoryIcon' },
    { name: 'nav_student_installment_schedule', path: APP_PATHS.STUDENT.INSTALLMENT_SCHEDULE, icon: 'InstallmentIcon' },
    { name: 'nav_student_download_receipts', path: APP_PATHS.STUDENT.DOWNLOAD_RECEIPTS, icon: 'ReceiptsIcon' },
    { name: 'nav_student_notifications', path: APP_PATHS.STUDENT.NOTIFICATIONS, icon: 'AnnounceIcon' },
    { name: 'nav_student_submit_feedback', path: APP_PATHS.STUDENT.SUBMIT_FEEDBACK, icon: 'FeedbackIcon' },
];

/**
 * Ordered prefix-to-fallback mapping.
 * Prefixes are derived from APP_PATHS (no magic strings) so any dynamic role
 * whose defaultPath starts with a known shell prefix gets the correct fallback
 * automatically — zero code changes required when new roles are created.
 *
 * Resolution order matters: more-specific prefixes should appear first.
 */
export const SHELL_PREFIX_MAP: ReadonlyArray<{ prefix: string; fallback: FallbackNavLink[] }> = [
    { prefix: SHELL_PREFIXES.ADMIN,   fallback: ADMIN_FALLBACK },
    { prefix: SHELL_PREFIXES.STAFF,   fallback: STAFF_FALLBACK },
    { prefix: SHELL_PREFIXES.STUDENT, fallback: STUDENT_FALLBACK },
];
